/**
 * SimpleRichText — Lightweight rich text editor for form fields.
 *
 * Supports: Bold, Italic, Underline, Bullet list, Numbered list, Alignment.
 * Uses contentEditable + execCommand (no external lib).
 * Stores HTML. Auto-save via onBlur. Placeholder support.
 *
 * Usage:
 *   <SimpleRichText
 *     defaultValue={product.objective ?? ''}
 *     onBlur={html => save('objective', html || null)}
 *     placeholder="Ex : À l'issue de cette formation..."
 *     disabled={!canEdit}
 *   />
 */

import { useRef, useState, useCallback, useEffect } from 'react'
import {
    Bold, Italic, Underline, List, ListOrdered,
    AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react'
import { Toggle } from '@/components/ui/toggle'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

interface SimpleRichTextProps {
    /** Initial HTML content */
    defaultValue?: string
    /** Called with HTML content on blur */
    onBlur?: (html: string) => void
    /** Called with HTML content on every input (formulaires à bouton « Enregistrer »
     *  qui lisent la valeur au submit sans attendre le blur). */
    onChange?: (html: string) => void
    /** Placeholder text (shown when empty) */
    placeholder?: string
    /** Read-only mode */
    disabled?: boolean
    /** Minimum height in rows (1 row ≈ 24px) */
    rows?: number
    /** Additional className */
    className?: string
}

// Toolbar button definition
interface ToolbarAction {
    command: string
    icon: typeof Bold
    label: string
    value?: string
}

const FORMAT_ACTIONS: ToolbarAction[] = [
    { command: 'bold', icon: Bold, label: 'Bold' },
    { command: 'italic', icon: Italic, label: 'Italic' },
    { command: 'underline', icon: Underline, label: 'Underline' },
]

const LIST_ACTIONS: ToolbarAction[] = [
    { command: 'insertUnorderedList', icon: List, label: 'Bullet list' },
    { command: 'insertOrderedList', icon: ListOrdered, label: 'Numbered list' },
]

const ALIGN_ACTIONS: ToolbarAction[] = [
    { command: 'justifyLeft', icon: AlignLeft, label: 'Align left' },
    { command: 'justifyCenter', icon: AlignCenter, label: 'Align center' },
    { command: 'justifyRight', icon: AlignRight, label: 'Align right' },
]

export function SimpleRichText({
    defaultValue = '',
    onBlur,
    onChange,
    placeholder,
    disabled = false,
    rows = 3,
    className,
}: SimpleRichTextProps) {
    const editorRef = useRef<HTMLDivElement>(null)
    const [isEmpty, setIsEmpty] = useState(!defaultValue)
    const [isFocused, setIsFocused] = useState(false)
    const initializedRef = useRef(false)

    // Initialize content once
    useEffect(() => {
        if (editorRef.current && !initializedRef.current) {
            editorRef.current.innerHTML = defaultValue
            setIsEmpty(!defaultValue || defaultValue === '<br>')
            initializedRef.current = true
        }
    }, [defaultValue])

    // Execute formatting command
    const execCommand = useCallback((command: string, value?: string) => {
        document.execCommand(command, false, value)
        editorRef.current?.focus()
    }, [])

    // Check if content is empty (only whitespace/br/empty tags)
    const checkEmpty = useCallback(() => {
        if (!editorRef.current) return true
        const text = editorRef.current.textContent?.trim() ?? ''
        return text.length === 0
    }, [])

    // Handle input
    const handleInput = useCallback(() => {
        const empty = checkEmpty()
        setIsEmpty(empty)
        if (onChange && editorRef.current) {
            onChange(empty ? '' : editorRef.current.innerHTML)
        }
    }, [checkEmpty, onChange])

    // Handle blur → save
    const handleBlur = useCallback(() => {
        setIsFocused(false)
        if (!editorRef.current || !onBlur) return
        const html = checkEmpty() ? '' : editorRef.current.innerHTML
        onBlur(html)
    }, [onBlur, checkEmpty])

    const handleFocus = useCallback(() => {
        setIsFocused(true)
    }, [])

    // Keyboard shortcuts
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            execCommand('bold')
        } else if (e.key === 'i' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            execCommand('italic')
        } else if (e.key === 'u' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            execCommand('underline')
        }
    }, [execCommand])

    const minHeight = Math.max(rows * 24, 72)

    return (
        <div
            className={cn(
                'rounded-md border border-input bg-background transition-colors',
                isFocused && 'ring-2 ring-ring ring-offset-2',
                disabled && 'opacity-50 cursor-not-allowed',
                className,
            )}
        >
            {/* Toolbar */}
            {!disabled && (
                <div className="flex items-center gap-0.5 px-1.5 py-1 border-b bg-muted/30 rounded-t-md flex-wrap">
                    {FORMAT_ACTIONS.map(action => (
                        <Toggle
                            key={action.command}
                            size="sm"
                            className="h-7 w-7 p-0"
                            onPressedChange={() => execCommand(action.command)}
                            aria-label={action.label}
                        >
                            <action.icon className="h-3.5 w-3.5" />
                        </Toggle>
                    ))}
                    <Separator orientation="vertical" className="mx-1 h-5" />
                    {LIST_ACTIONS.map(action => (
                        <Toggle
                            key={action.command}
                            size="sm"
                            className="h-7 w-7 p-0"
                            onPressedChange={() => execCommand(action.command)}
                            aria-label={action.label}
                        >
                            <action.icon className="h-3.5 w-3.5" />
                        </Toggle>
                    ))}
                    <Separator orientation="vertical" className="mx-1 h-5" />
                    {ALIGN_ACTIONS.map(action => (
                        <Toggle
                            key={action.command}
                            size="sm"
                            className="h-7 w-7 p-0"
                            onPressedChange={() => execCommand(action.command)}
                            aria-label={action.label}
                        >
                            <action.icon className="h-3.5 w-3.5" />
                        </Toggle>
                    ))}
                </div>
            )}

            {/* Editor area */}
            <div className="relative">
                {/* Placeholder */}
                {isEmpty && !isFocused && placeholder && (
                    <div
                        className="absolute inset-0 px-3 py-2 text-base md:text-sm text-muted-foreground/60 italic pointer-events-none overflow-hidden"
                        aria-hidden
                    >
                        {placeholder}
                    </div>
                )}

                {/* Content editable */}
                <div
                    ref={editorRef}
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    className={cn(
                        // text-base sur mobile (16px) évite le zoom auto iOS au focus
                        // d'un champ éditable < 16px ; md:text-sm restaure 14px desktop.
                        // (utilitaire > couche `components` de prose-sm → il l'emporte.)
                        'px-3 py-2 text-base md:text-sm outline-none overflow-y-auto',
                        'prose prose-sm max-w-none',
                        '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
                        '[&_li]:my-0.5',
                        disabled && 'pointer-events-none',
                    )}
                    style={{ minHeight }}
                    onInput={handleInput}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    role="textbox"
                    aria-multiline="true"
                    aria-placeholder={placeholder}
                />
            </div>
        </div>
    )
}
